
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.errorElement = this.querySelector('[data-predictive-search-error]');
    this.catalogIndexUrl = this.dataset.catalogIndexUrl;
    const configuredSidebarLimit = Number.parseInt(this.dataset.maxResultsInSidebar, 10);
    this.maxResultsInSidebar = Number.isFinite(configuredSidebarLimit)
      ? Math.min(12, Math.max(1, configuredSidebarLimit))
      : 4;
    this.catalogProducts = null;
    this.catalogLoadPromise = null;
    this.instanceId = (this.closest('search-drawer')?.id || this.id || 'SearchTemplate').replace(/[^a-zA-Z0-9_-]/g, '');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    this.hideError();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up')
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[data-predictive-search-option][aria-selected="true"]');
    const allElements = this.querySelectorAll('[data-predictive-search-option]');
    let activeElement = allElements[0];

    if (!activeElement) return;

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();
    this.abortController?.abort();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.catalogIndexUrl) {
      try {
        const catalogProducts = await this.getCatalogProducts();
        if (this.getQuery() !== searchTerm || !this.isConnected) return;
        this.renderCatalogResults(this.rankCatalogMatches(catalogProducts, searchTerm), searchTerm);
      } catch (error) {
        if (this.getQuery() !== searchTerm || !this.isConnected) return;
        this.showError('Search catalogue could not load. Please try again.');
      }
      return;
    }

    this.abortController = new AbortController();
    const searchableFields = 'title,product_type,variants.title,vendor,tag,body';
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,query&${encodeURIComponent('resources[limit]')}=6&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent(searchableFields)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const responseSection = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!responseSection) throw new Error('Predictive search response is missing its section');
        const resultsMarkup = responseSection.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.showError();
      });
  }

  async getCatalogProducts() {
    if (this.catalogProducts) return this.catalogProducts;
    if (this.catalogLoadPromise) return this.catalogLoadPromise;

    this.catalogLoadPromise = (async () => {
      const products = [];
      let page = 1;
      let pages = 1;

      while (page <= pages) {
        const indexUrl = new URL(this.catalogIndexUrl, window.location.origin);
        indexUrl.searchParams.set('page', page);
        const response = await fetch(indexUrl.toString());
        if (!response.ok) throw new Error(`Catalog index request failed: ${response.status}`);

        const payload = await response.json();
        if (!Array.isArray(payload.products) || !Number.isInteger(payload.pages) || payload.pages < 1) {
          throw new Error('Catalog index response is invalid');
        }

        pages = payload.pages;
        if (pages > 1000) throw new Error('Catalog index exceeds the supported page bound');
        products.push(...payload.products);
        page += 1;
      }

      this.catalogProducts = products;
      return products;
    })();

    try {
      return await this.catalogLoadPromise;
    } finally {
      if (!this.catalogProducts) this.catalogLoadPromise = null;
    }
  }

  rankCatalogMatches(products, searchTerm) {
    const query = this.normalizeSearchText(searchTerm);
    return products.map((product) => {
      const searchableDescription = this.getSearchableDescription(product.description);
      const fields = [
        ['title', 'Title', this.stripMarkup(product.title).trim()],
        ['material', 'Material', this.stripMarkup(product.material).trim()],
        ['material-description', 'Material', this.getMaterialDescription(searchableDescription)],
        ['description', 'Description', searchableDescription],
        ['metadata', 'Metadata', this.stripMarkup(product.metadata).trim()],
      ];
      for (let rank = 0; rank < fields.length; rank += 1) {
        const [field, label, fieldValue] = fields[rank];
        const value = String(fieldValue || '');
        if (this.findPrefixMatch(value, query) !== -1) return { product, field, label, rank, value };
      }
      return null;
    }).filter(Boolean).sort((left, right) => left.rank - right.rank || left.product.title.localeCompare(right.product.title));
  }

  getSearchableDescription(value) {
    const lines = this.stripMarkup(value).split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const outfitStart = lines.findIndex((line) => /^(?:to\s+)?complete\s+(?:this|the)\s+outfit\b/i.test(line));
    const descriptionLines = outfitStart === -1 ? lines : lines.slice(0, outfitStart);
    return descriptionLines.filter((line) => !/^ships?\s+in\b/i.test(line)).join(' ');
  }

  getMaterialDescription(description) {
    return this.stripMarkup(description)
      .split(/\n|(?<=[.!?])\s+/)
      .filter((line) => /\b(wool|cotton|silk|denim|linen|leather|cashmere|care|clean|composition)\b/i.test(line))
      .join(' ');
  }

  stripMarkup(value) {
    const template = document.createElement('template');
    template.innerHTML = String(value ?? '');
    return template.content.textContent || '';
  }

  normalizeSearchText(value) {
    return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  }

  findPrefixMatch(value, normalizedQuery) {
    if (!normalizedQuery) return -1;
    const normalizedValue = this.normalizeSearchText(value);
    const matcher = new RegExp(`(^|[^\\p{L}\\p{N}])(${normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'u');
    const match = matcher.exec(normalizedValue);
    return match ? match.index + match[1].length : -1;
  }

  renderCatalogResults(matches, searchTerm) {
    this.predictiveSearchResults.replaceChildren();
    const resultList = document.createElement('ul');
    resultList.className = 'predictive-search__results-list list-unstyled';
    resultList.setAttribute('role', 'listbox');
    const safeMatches = matches.map((match) => ({ ...match, productUrl: this.getSafeProductUrl(match.product.url) }))
      .filter(({ productUrl }) => productUrl);
    const visibleMatches = safeMatches.slice(0, this.maxResultsInSidebar);

    if (!visibleMatches.length) {
      const noResults = document.createElement('p');
      noResults.dataset.predictiveSearchNoResults = '';
      noResults.textContent = `No products found for “${searchTerm}”.`;
      this.predictiveSearchResults.append(noResults);
    } else {
      visibleMatches.forEach(({ product, field, value, productUrl }) => {
        const item = document.createElement('li');
        item.className = 'predictive-search__list-item';
        item.dataset.predictiveSearchOption = '';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        const link = document.createElement('a');
        link.href = productUrl;
        link.className = 'predictive-search__item predictive-search__item--link link--text';
        link.tabIndex = -1;
        if (product.image) {
          const image = document.createElement('img');
          image.className = 'predictive-search__image w-100';
          image.src = product.image;
          image.alt = product.imageAlt || product.title;
          image.width = 60;
          image.height = 72;
          image.loading = 'eager';
          image.decoding = 'async';
          link.append(image);
        }
        const content = document.createElement('div');
        content.className = 'predictive-search__item-content';
        const title = document.createElement('h3');
        title.className = 'predictive-search__item-heading product-card__name';
        const productTitle = this.stripMarkup(product.title).trim();
        if (field === 'title') this.appendHighlightedText(title, productTitle, searchTerm);
        else title.textContent = productTitle;
        content.append(title);
        const productPrice = this.stripMarkup(product.price).trim();
        if (productPrice) {
          const price = document.createElement('p');
          price.className = 'price-item price-item--regular';
          price.textContent = productPrice;
          content.append(price);
        }
        link.append(content);
        item.append(link);
        resultList.append(item);
      });
      this.predictiveSearchResults.append(resultList);
      if (safeMatches.length > visibleMatches.length) {
        const showAllLink = document.createElement('a');
        showAllLink.className = 'ta-search-drawer__show-all';
        showAllLink.href = this.getSearchResultsUrl(searchTerm);
        showAllLink.textContent = `SHOW ALL RESULTS (${safeMatches.length})`;
        this.predictiveSearchResults.append(showAllLink);
      }
    }

    const count = document.createElement('span');
    count.dataset.predictiveSearchLiveRegionCountValue = '';
    count.className = 'visually-hidden';
    count.textContent = safeMatches.length
      ? (safeMatches.length > visibleMatches.length
        ? `${visibleMatches.length} of ${safeMatches.length} product results shown.`
        : `${safeMatches.length} product results found.`)
      : 'No product results found.';
    this.predictiveSearchResults.append(count);
    this.finishRenderingResults();
  }

  getSearchResultsUrl(searchTerm) {
    const formAction = this.querySelector('form.search')?.action || '/search';
    const url = new URL(formAction, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url.href;
  }

  getSafeProductUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return null;
      return url.href;
    } catch {
      return null;
    }
  }

  appendHighlightedText(target, value, searchTerm, maximumLength = 0) {
    const text = this.stripMarkup(value);
    const matchIndex = this.findPrefixMatch(text, this.normalizeSearchText(searchTerm));
    if (matchIndex === -1) {
      target.append(text);
      return;
    }
    const start = maximumLength ? Math.max(0, matchIndex - Math.floor(maximumLength / 3)) : 0;
    const end = maximumLength ? Math.min(text.length, start + maximumLength) : text.length;
    if (start) target.append('…');
    const localMatchIndex = matchIndex - start;
    const matchEnd = Math.min(end, localMatchIndex + searchTerm.length);
    target.append(text.slice(start, start + localMatchIndex));
    const emphasis = document.createElement('strong');
    emphasis.className = 'predictive-search__match-highlight';
    emphasis.textContent = text.slice(start + localMatchIndex, start + matchEnd);
    target.append(emphasis, text.slice(start + matchEnd, end));
    if (end < text.length) target.append('…');
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.finishRenderingResults();
  }

  finishRenderingResults() {
    this.predictiveSearchResults.querySelectorAll('[data-predictive-search-option]').forEach((option, index) => {
      option.id = `PredictiveSearchOption-${this.instanceId}-${index + 1}`;
    });
    this.setAttribute('results', true);
    this.hideError();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  showError(message) {
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    if (this.errorElement) {
      if (message) this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }
    this.setLiveRegionText(this.errorElement?.textContent.trim() || 'Search is temporarily unavailable.');
    this.isOpen = false;
  }

  hideError() {
    if (this.errorElement) this.errorElement.hidden = true;
  }

  close(clearSearchTerm = false) {
    this.abortController?.abort();
    this.abortController = null;
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.hideError();
    }

    const selected = this.querySelector('[aria-selected="true"]');
    

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
